/**
 * "Export CSV" / "Export Excel" on the Tickets and Volumes pages (operator Reports (a),
 * migration 0241): the page's own query, downloaded as a spreadsheet. On a plan without
 * OP_REPORTS the buttons are replaced by a plain note (no plan names, §58).
 */

import React from 'react';
import type { OperatorPortalMeResponse } from '@lodex-pro/shared/validation/operator-portal-me';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalExport } from '../hooks/useOperatorPortal';
import { NOT_ON_PLAN, portalLacks } from '../nav/portal-features';

export const EXPORT_TOO_LARGE_MESSAGE =
  'That export has too many tickets. Choose a shorter date range and try again.';
export const EXPORT_FAILED_MESSAGE = 'Couldn’t make the export. Try again.';

/** Save a downloaded file under its own name. */
export function saveFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const BUTTON: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
  cursor: 'pointer',
};

export function ExportButtons({
  page,
  me,
  params,
}: {
  page: 'tickets' | 'volumes';
  me: OperatorPortalMeResponse | undefined;
  params: Record<string, string | undefined>;
}): React.JSX.Element {
  const exp = usePortalExport(page);

  if (portalLacks(me, 'OP_REPORTS')) {
    return (
      <span style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>
        Export to CSV or Excel: {NOT_ON_PLAN.toLowerCase()}
      </span>
    );
  }

  const run = (format: 'csv' | 'xlsx'): void => {
    exp.mutate({ ...params, format }, { onSuccess: (file) => saveFile(file.filename, file.blob) });
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <button
        type="button"
        style={BUTTON}
        disabled={exp.isPending}
        onClick={(): void => run('csv')}
      >
        Export CSV
      </button>
      <button
        type="button"
        style={BUTTON}
        disabled={exp.isPending}
        onClick={(): void => run('xlsx')}
      >
        Export Excel
      </button>
      {exp.isPending && (
        <span role="status" style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>
          Preparing…
        </span>
      )}
      {exp.isError && (
        <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
          {exp.error instanceof ApiError && exp.error.code === 'OPERATOR_REPORT_TOO_LARGE'
            ? EXPORT_TOO_LARGE_MESSAGE
            : EXPORT_FAILED_MESSAGE}
        </span>
      )}
    </div>
  );
}
